import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { CheckCircle, XCircle, Radio, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  approved: "text-[#B026FF] bg-[#B026FF]/10 border-[#B026FF]/20",
  restricted: "text-red-400 bg-red-400/10 border-red-400/20",
};

export default function AdminStations() {
  const [stations, setStations] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStations(); }, []);

  const loadStations = async () => {
    try {
      // No orderBy — legacy docs without createdAt are excluded by Firestore orderBy
      const snap = await getDocs(collection(db, "stations"));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setStations(items);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, "stations", id), { status });
    // Notify the station creator
    const station = stations.find((s) => s.id === id);
    if (station?.creatorId) {
      const message =
        status === "approved"
          ? `Your station "${station.name}" has been approved and is now live!`
          : `Your station "${station.name}" has been restricted. Contact stagepassadmin@gmail.com.`;
      try {
        await addDoc(collection(db, "notifications", station.creatorId, "items"), {
          type: status === "approved" ? "station_approved" : "station_restricted",
          message,
          read: false,
          createdAt: serverTimestamp(),
        });
      } catch (e) { console.error("Notification write error:", e); }
    }
    setStations((s) => s.map((x) => x.id === id ? { ...x, status } : x));
  };

  const filtered = stations.filter((s) => filter === "all" || s.status === filter);

  return (
    <div data-testid="admin-stations" className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} /> Admin</Link>
        <h2 className="text-xl font-black text-white">Stations</h2>
      </div>

      <div className="flex gap-2">
        {["pending", "approved", "restricted", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${filter === s ? "bg-[#B026FF]/20 text-[#B026FF] border border-[#B026FF]/30" : "bg-[#111] border border-white/10 text-zinc-400 hover:border-white/20"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">No stations with status: {filter}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((station) => (
            <div key={station.id} data-testid={`station-row-${station.id}`}
              className="flex items-center gap-4 bg-[#111] border border-white/5 rounded-xl p-4">
              <div className="w-10 h-10 rounded-xl bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                {station.logoURL ? (
                  <img src={station.logoURL} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Radio size={16} className="text-zinc-600" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{station.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${STATUS_COLOR[station.status] || STATUS_COLOR.pending}`}>
                    {station.status || "pending"}
                  </span>
                  {station.genre && <span className="text-zinc-600 text-xs">{station.genre}</span>}
                  {station.autodjEnabled && <span className="text-[10px] text-[#B026FF] bg-[#B026FF]/10 px-1.5 py-0.5 rounded-full">Auto-DJ</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/radio/${station.id}`} className="text-zinc-500 hover:text-white text-xs border border-white/10 px-2 py-1 rounded-lg hover:border-white/30 transition-colors">
                  View
                </Link>
                {station.status !== "approved" && (
                  <button onClick={() => updateStatus(station.id, "approved")} data-testid={`approve-station-${station.id}`}
                    className="flex items-center gap-1 text-xs bg-[#B026FF]/10 border border-[#B026FF]/20 text-[#B026FF] px-3 py-1.5 rounded-lg hover:bg-[#B026FF]/20 transition-colors">
                    <CheckCircle size={12} /> Approve
                  </button>
                )}
                {station.status !== "restricted" && (
                  <button onClick={() => updateStatus(station.id, "restricted")} data-testid={`restrict-station-${station.id}`}
                    className="flex items-center gap-1 text-xs bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500/20 transition-colors">
                    <XCircle size={12} /> Restrict
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
